import { PAGINAS_DOCS } from '../content/registry';

/**
 * Buscador del centro de ayuda.
 *
 * Sin dependencias y sin servidor. El indice son doce paginas, cabe entero en
 * memoria, y lo que de verdad hace falta -ignorar tildes y mayusculas, y
 * ordenar por donde encaja lo escrito- son treinta lineas. Una dependencia mas
 * es una dependencia mas que auditar, actualizar y cargar en el navegador.
 *
 * Se indexa el titulo, la descripcion y las CLAVES de cada pagina. Las claves
 * existen justo para esto: quien busca «contrasena» no sabe todavia que aqui
 * eso se llama cuenta de ElectroDavisan, y aun asi tiene que llegar.
 */

export interface Resultado {
  slug: string;
  titulo: string;
  descripcion: string;
  seccion: string;
  ruta: string;
  puntos: number;
}

/** Quita tildes y pasa a minusculas: «Auditoria» y «auditoria» son lo mismo. */
export const normalizar = (texto: unknown): string =>
  String(texto ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

/**
 * El indice, construido una sola vez.
 *
 * Cada entrada guarda su texto ya normalizado. Normalizar en cada pulsacion
 * seria rehacer el mismo trabajo con cada letra que alguien escribe.
 */
const INDICE = PAGINAS_DOCS.map((pagina) => ({
  slug: pagina.slug,
  titulo: pagina.titulo,
  descripcion: pagina.descripcion,
  seccion: pagina.seccion,
  ruta: pagina.ruta,
  _titulo: normalizar(pagina.titulo),
  _descripcion: normalizar(pagina.descripcion),
  _claves: pagina.claves.map(normalizar),
  _seccion: normalizar(pagina.seccion),
}));

type Entrada = (typeof INDICE)[number];

/**
 * Cuanto encaja un termino con una entrada. 0 = no encaja.
 *
 * Los pesos ordenan por lo que la gente espera encontrar arriba: el titulo
 * exacto primero, luego el que empieza igual, luego las claves, y al final la
 * descripcion. Sin esto, buscar «ticket» podria devolver antes una pagina que
 * los menciona de pasada que la de crear un ticket.
 */
function puntuarTermino(entrada: Entrada, termino: string): number {
  if (!termino) return 0;

  if (entrada._titulo === termino) return 100;
  if (entrada._titulo.startsWith(termino)) return 70;
  if (entrada._titulo.includes(termino)) return 50;

  if (entrada._claves.some((clave) => clave === termino)) return 45;
  if (entrada._claves.some((clave) => clave.startsWith(termino))) return 32;
  if (entrada._claves.some((clave) => clave.includes(termino))) return 22;

  if (entrada._descripcion.includes(termino)) return 14;
  if (entrada._seccion.includes(termino)) return 8;

  return 0;
}

/**
 * Busca.
 *
 * Con varias palabras se exigen TODAS. Buscar «crear ticket» pidiendo
 * cualquiera de las dos devolveria media ayuda, y quien escribe dos palabras
 * esta afinando, no ampliando.
 */
export function buscarEnDocs(consulta: string, limite = 8): Resultado[] {
  const terminos = normalizar(consulta).split(/\s+/).filter(Boolean);
  if (!terminos.length) return [];

  const resultados: Resultado[] = [];
  for (const entrada of INDICE) {
    let total = 0;
    let todos = true;

    for (const termino of terminos) {
      const puntos = puntuarTermino(entrada, termino);
      if (!puntos) {
        todos = false;
        break;
      }
      total += puntos;
    }

    if (todos) {
      resultados.push({
        slug: entrada.slug,
        titulo: entrada.titulo,
        descripcion: entrada.descripcion,
        seccion: entrada.seccion,
        ruta: entrada.ruta,
        puntos: total,
      });
    }
  }

  // A igualdad de puntos manda el orden del menu, que es el orden de lectura.
  const orden = new Map(PAGINAS_DOCS.map((p, i) => [p.slug, i]));
  resultados.sort(
    (a, b) => b.puntos - a.puntos || (orden.get(a.slug) ?? 0) - (orden.get(b.slug) ?? 0),
  );

  return resultados.slice(0, limite);
}
